import React from 'react';
import { CheckCircle2, ShieldCheck, Database, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * Trust-source badge — one shared taxonomy for part rows and Q&A footers,
 * placed next to the probability so the two trust signals (where the data
 * came from, how confident the match is) read together:
 *
 *  - epc:        green check   — manufacturer's parts catalogue; number,
 *                               description and fitment are authoritative.
 *  - manual:     violet shield — the organisation's reference documents
 *                               (user manual / maintenance guide); certified,
 *                               a visible step below EPC.
 *  - datasource: violet-blue   — the organisation's own uploaded data
 *                               sources (price lists, supplier files).
 *  - internet:   amber warning — found on the open web; unverified,
 *                               double-check before ordering.
 */
export type SourceKind = 'epc' | 'manual' | 'datasource' | 'internet';

const KIND_STYLE: Record<SourceKind, { bg: string; fg: string; border: string }> = {
  epc: { bg: '#ecfdf5', fg: '#047857', border: '#a7f3d0' },
  manual: { bg: '#eef2ff', fg: '#4f46e5', border: '#c7d2fe' },
  datasource: { bg: '#eff6ff', fg: '#1d4ed8', border: '#bfdbfe' },
  internet: { bg: '#fffbeb', fg: '#8a5a00', border: '#fde68a' },
};

const KIND_ICON: Record<SourceKind, React.ComponentType<any>> = {
  epc: CheckCircle2,
  manual: ShieldCheck,
  datasource: Database,
  internet: AlertTriangle,
};

const SourceBadge: React.FC<{
  kind: SourceKind;
  /** Override the default label (e.g. a specific document title). */
  label?: string;
  /** Compact: icon-only round badge (for tight table columns). */
  compact?: boolean;
}> = ({ kind, label, compact = false }) => {
  const { t } = useTranslation();
  const style = KIND_STYLE[kind];
  const Icon = KIND_ICON[kind];
  const defaultLabels: Record<SourceKind, string> = {
    epc: t('chat.sourceBadge.epc', { defaultValue: 'Genuine EPC' }),
    manual: t('chat.sourceBadge.manual', { defaultValue: 'User manual' }),
    datasource: t('chat.sourceBadge.datasource', { defaultValue: 'Your data' }),
    internet: t('chat.sourceBadge.internet', { defaultValue: 'Internet' }),
  };
  const hints: Record<SourceKind, string> = {
    epc: t('chat.sourceBadge.epcHint', {
      defaultValue: "From the manufacturer's parts catalogue — number, description and fitment are authoritative.",
    }),
    manual: t('chat.sourceBadge.manualHint', {
      defaultValue: "From your organisation's reference documents — verified data.",
    }),
    datasource: t('chat.sourceBadge.datasourceHint', {
      defaultValue: "From your organisation's own uploaded data sources.",
    }),
    internet: t('chat.sourceBadge.internetHint', {
      defaultValue: 'Found on the open web — unverified, confirm before ordering.',
    }),
  };

  if (compact) {
    return (
      <span
        title={`${label || defaultLabels[kind]} — ${hints[kind]}`}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: '22px', height: '22px', borderRadius: '50%',
          backgroundColor: style.bg, color: style.fg, border: `1px solid ${style.border}`,
        }}
      >
        <Icon size={12} />
      </span>
    );
  }
  return (
    <span
      title={`${label || defaultLabels[kind]} — ${hints[kind]}`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '4px',
        padding: '2px 8px', borderRadius: '9999px',
        fontSize: '11px', fontWeight: 600,
        // Never bleed into the neighbouring column: the pill caps at its
        // container and the label ellipsizes (full text stays on hover).
        maxWidth: '100%', minWidth: 0,
        backgroundColor: style.bg, color: style.fg, border: `1px solid ${style.border}`,
      }}
    >
      <Icon size={12} style={{ flexShrink: 0 }} />
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {label || defaultLabels[kind]}
      </span>
    </span>
  );
};

export default SourceBadge;
